// src/screens/UpdateEvent2.jsx
// 🆕 VERSION RÉCAP — affiche directement TOUTES les valeurs actuelles
// de l'événement. Clique sur le crayon ✏️ à côté d'un champ pour le
// modifier SEULEMENT lui (édition en ligne), pas besoin de repasser
// par tout un parcours séquentiel comme à la création.

import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import { getEventById, updateEvent, getCategories } from "../services/CreateurService";
import RichTextEditor from "../components/RichTextEditor";
import { Loader2, Pencil, X, Check, Plus, Trash2, Lock } from "lucide-react";
import { getVerrouEvenement } from "../services/DemandeService";
import ModaleDemande from "../components/ModaleDemande";
import { peutAgirPour, getCreateurActif, getTokenPublication } from "../utils/agirPour";

// Extrait le premier nombre trouvé dans un texte libre.
// Ex : "à partir de 99 €" → "99" | "15,50 euros" → "15.50"
function extraireNombreDuTexte(texte) {
    if (!texte) return "";
    const match = String(texte).match(/(\d+[.,]?\d*)/);
    if (!match) return "";
    return match[1].replace(",", ".");
}

const MIN_IMAGES = 1;
const SECONDARY_IMAGES = 4;
const TAILLE_MAX_IMAGE = 2 * 1024 * 1024; // 2 Mo

const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, "").trim();

// ══════════════════════════════════════════════════════════
//  RÈGLES DE VALIDATION PAR CHAMP
// ══════════════════════════════════════════════════════════

const REGLES = {
    nom: {
        regex: /^(?=.*[A-Za-zÀ-ÿ0-9]).{3,100}$/,
        maxLength: 100,
        message: "Le nom doit contenir entre 3 et 100 caractères.",
    },
    sousnom: {
        regex: /^[A-Za-zÀ-ÿ'-]+(\s[A-Za-zÀ-ÿ'-]+)?$/,
        maxLength: 40,
        message: "Le sous-titre doit contenir 1 ou 2 mots maximum (lettres uniquement).",
    },
    duree: {
        regex: /^(?=.*[0-9])[0-9A-Za-zÀ-ÿ\s]{1,30}$/,
        maxLength: 30,
        message: "Indique une durée avec au moins un chiffre (ex : 2h30, 1 jour, 45 min).",
    },
    cp: {
        regex: /^\d{5}$/,
        maxLength: 5,
        message: "Le code postal doit contenir exactement 5 chiffres.",
    },
    ville: {
        regex: /^[A-Za-zÀ-ÿ\s'-]{2,100}$/,
        maxLength: 100,
        message: "La ville ne doit contenir que des lettres (2 à 100 caractères).",
    },
    rue: {
        regex: /^[A-Za-zÀ-ÿ0-9\s'.,-]{5,150}$/,
        maxLength: 150,
        message: "L'adresse doit contenir entre 5 et 150 caractères valides.",
    },
    billetterie_lien: {
        regex: /^https?:\/\/[^\s]+\.[^\s]{2,}$/i,
        maxLength: 500,
        message: "Le lien doit être une URL valide (ex : https://exemple.fr).",
    },
    lien_reunion: {
        regex: /^https?:\/\/[^\s]+\.[^\s]{2,}$/i,
        maxLength: 500,
        message: "Le lien doit être une URL valide (ex : https://meet.google.com/xxx).",
    },
    tarif_titre: {
        regex: /^[A-Za-zÀ-ÿ0-9\s'-]{2,50}$/,
        maxLength: 50,
        message: "Le titre du tarif doit contenir entre 2 et 50 caractères.",
    },
    description: {
        regex: /^(?=.*[A-Za-zÀ-ÿ]).{20,2000}$/s,
        maxLength: 2000,
        message: "La description doit contenir entre 20 et 2000 caractères.",
    },
};

function valider(regleId, valeur) {
    const regle = REGLES[regleId];
    if (!regle) return true;
    if (!valeur || !valeur.trim()) {
        toast.error("Ce champ est obligatoire.");
        return false;
    }
    if (valeur.length > regle.maxLength) {
        toast.error(`Maximum ${regle.maxLength} caractères autorisés.`);
        return false;
    }
    if (!regle.regex.test(valeur.trim())) {
        toast.error(regle.message);
        return false;
    }
    return true;
}

// ══════════════════════════════════════════════════════════
//  BULLE DE CONVERSATION — question (bot, gauche) + réponse
//  (utilisateur, droite) avec crayon pour modifier UNIQUEMENT
//  cette réponse. Aucune cascade vers les questions suivantes :
//  chaque champ sauvegarde son propre état et referme l'édition,
//  point final.
// ══════════════════════════════════════════════════════════

const AvatarBot = () => (
    <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
        style={{ backgroundColor: "#481699" }}
    >
        🎪
    </div>
);

// 👇 Simplifiée : la bulle affiche TOUJOURS la valeur (lecture seule).
// Cliquer sur le crayon n'ouvre plus un formulaire inline dans la bulle —
// ça ouvre l'éditeur dans la zone de saisie fixée en bas (comme AddEvent2).
// `actif` met juste un liseré pour montrer quelle bulle est en cours
// de modification.
// verrouille = true → cadenas au lieu du crayon (billets vendus)
const LigneRecap = ({ label, onEdit, testId, actif, verrouille, onVerrou, children }) => (
    <div className="flex flex-col gap-2">
        {/* ── Question (bot, gauche) ── */}
        <div className="flex items-end gap-2 justify-start">
            <AvatarBot />
            <div className="px-4 py-2.5 text-sm leading-relaxed bg-white/10 text-white rounded-2xl rounded-bl-md border border-white/10 max-w-[75%] min-w-0 break-words [overflow-wrap:anywhere]">
                {label}
            </div>
        </div>

        {/* ── Réponse (utilisateur, droite) + crayon ── */}
        <div className="flex items-end gap-2 justify-end min-w-0">
            {verrouille ? (
                <button
                    onClick={onVerrou}
                    data-testid={`${testId}-verrou`}
                    className="mb-1 w-6 h-6 flex items-center justify-center rounded-full text-amber-300/80 hover:text-amber-200 hover:bg-white/10 transition shrink-0"
                    title="Billets vendus : contactez Thrundrz pour modifier"
                >
                    <Lock size={12} />
                </button>
            ) : onEdit && (
                <button
                    onClick={onEdit}
                    data-testid={testId}
                    className="mb-1 w-6 h-6 flex items-center justify-center rounded-full text-white/40 hover:text-white hover:bg-white/10 transition shrink-0"
                    title={`Modifier ${label.toLowerCase()}`}
                >
                    <Pencil size={12} />
                </button>
            )}
            <div
                className="max-w-[78%] min-w-0 px-4 py-2.5 text-sm leading-relaxed text-white rounded-2xl rounded-br-md break-words [overflow-wrap:anywhere] transition-all"
                style={{
                    backgroundColor: "#481699",
                    boxShadow: actif ? "0 0 0 2px #B48EF5" : "none",
                }}
            >
                {children || <span className="text-white/50">—</span>}
            </div>
        </div>
    </div>
);

// ── Bouton Enregistrer / Annuler générique pour une édition en ligne ──
const BarreEdition = ({ onSave, onCancel, saving }) => (
    <div className="flex gap-2 mt-3">
        <button
            onClick={onCancel}
            className="flex-1 py-2 rounded-xl bg-white/10 text-white/70 text-sm font-medium hover:bg-white/15 transition flex items-center justify-center gap-1"
        >
            <X size={15} /> Annuler
        </button>
        <button
            onClick={onSave}
            disabled={saving}
            className="flex-1 py-2 rounded-xl text-white text-sm font-semibold transition flex items-center justify-center gap-1 disabled:opacity-50"
            style={{ backgroundColor: "#481699" }}
        >
            <Check size={15} /> Enregistrer
        </button>
    </div>
);

// ══════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════

const UpdateEvent2 = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    // Consultant / admin dans l'espace /consultant → modifie pour le créateur choisi
    const agitPour = peutAgirPour();
    const createurActif = agitPour ? getCreateurActif() : null;
    const pageRetour = agitPour ? "/consultant" : "/EspaceClient/mesevents";
    const token = getTokenPublication();
    const envoiEnCoursRef = useRef(false);

    const [loadingEvent, setLoadingEvent] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Quel champ est en cours d'édition (null = aucun, tout est en lecture)
    const [edition, setEdition] = useState(null);
    const [saisie, setSaisie] = useState(""); // valeur temporaire pendant l'édition

    // ── Catégories ─────────────────────────────────────────
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [erreurCategories, setErreurCategories] = useState(false);

    const chargerCategories = () => {
        setLoadingCategories(true);
        setErreurCategories(false);
        getCategories()
            .then((cats) => setCategories(cats || []))
            .catch((err) => {
                console.error("Erreur chargement catégories :", err);
                setErreurCategories(true);
            })
            .finally(() => setLoadingCategories(false));
    };

    useEffect(() => {
        if (edition === "category" && categories.length === 0 && !loadingCategories && !erreurCategories) {
            chargerCategories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edition]);

    // ── Données de l'événement ───────────────────────────────
    const [images, setImages] = useState(Array(MIN_IMAGES + SECONDARY_IMAGES).fill(null));
    const [previewUrls, setPreviewUrls] = useState(Array(MIN_IMAGES + SECONDARY_IMAGES).fill(null));

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [nomEven, setNomEven] = useState("");
    const [sousNomEven, setSousNomEven] = useState("");
    const [dateTimeEven, setDateTimeEven] = useState("");
    const [duree, setDuree] = useState("");
    const [isGratuit, setIsGratuit] = useState(null);

    const [tarifs, setTarifs] = useState([]);
    // Tarifs en cours d'édition (copie de travail, validée seulement au clic "Enregistrer")
    const [tarifsEdition, setTarifsEdition] = useState([]);
    const [nouveauTarifTitre, setNouveauTarifTitre] = useState("");
    const [nouveauTarifPrix, setNouveauTarifPrix] = useState("");
    const [gratuitEdition, setGratuitEdition] = useState(false);

    // ── Présentiel / En ligne ────────────────────────────────
    const [typeEvent, setTypeEvent] = useState("Présentiel");
    const [lienReunion, setLienReunion] = useState("");
    const [typeEventEdition, setTypeEventEdition] = useState("Présentiel");
    const [lienReunionEdition, setLienReunionEdition] = useState("");

    const [codePostalEven, setCodePostalEven] = useState("");
    const [villeEven, setVilleEven] = useState("");
    const [rueEven, setRueEven] = useState("");
    // Copies de travail pour l'édition groupée de l'adresse
    const [cpEdition, setCpEdition] = useState("");
    const [villeEditionVal, setVilleEditionVal] = useState("");
    const [rueEditionVal, setRueEditionVal] = useState("");
    // Autocomplétion adresse (api-adresse.data.gouv.fr — officielle, gratuite)
    const [adresseQuery, setAdresseQuery] = useState("");
    const [suggestionsAdresse, setSuggestionsAdresse] = useState([]);
    const [rechercheAdresseEnCours, setRechercheAdresseEnCours] = useState(false);
    const [adresseManuelle, setAdresseManuelle] = useState(false);

    const [isRestaurant, setIsRestaurant] = useState(null);
    const [isParking, setIsParking] = useState(null);
    const [isAuditive, setIsAuditive] = useState(null);
    const [isHandicap, setIsHandicap] = useState(null);
    // Copies de travail pour l'édition groupée de l'accessibilité
    const [accesEdition, setAccesEdition] = useState({});

    const [billetterieType, setBilletterieType] = useState(null);
    const [lienBilletterie, setLienBilletterie] = useState("");
    const [nbrPlacesMax, setNbrPlacesMax] = useState("");
    const [billetterieTypeEdition, setBilletterieTypeEdition] = useState(null);
    const [lienEdition, setLienEdition] = useState("");

    const [description, setDescription] = useState("");

    // ── Verrou : billets vendus → date / lieu / tarifs via Thrundrz ──
    const [verrou, setVerrou] = useState({ verrouille: false, nb_reservations: 0, demande_en_cours: null });
    const [demande, setDemande] = useState(null); // null | "report" | "annulation" | "modification"

    const chargerVerrou = () =>
        getVerrouEvenement(id)
            .then(setVerrou)
            .catch((e) => console.error("Verrou :", e));

    useEffect(() => { chargerVerrou(); /* eslint-disable-next-line */ }, [id]);

    const bloque = verrou.verrouille;

    // ── Recherche d'adresse en direct (API officielle gouvernementale) ──
    // https://api-adresse.data.gouv.fr — France uniquement, gratuite, sans clé.
    useEffect(() => {
        if (edition !== "adresse" || adresseManuelle) return;
        if (adresseQuery.trim().length < 3) {
            setSuggestionsAdresse([]);
            return;
        }
        const controller = new AbortController();
        const timer = setTimeout(() => {
            setRechercheAdresseEnCours(true);
            fetch(
                `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(adresseQuery.trim())}&limit=10&autocomplete=1`,
                { signal: controller.signal }
            )
                .then((res) => res.json())
                .then((data) => setSuggestionsAdresse(data?.features || []))
                .catch((err) => {
                    if (err.name !== "AbortError") console.error("Erreur API adresse :", err);
                })
                .finally(() => setRechercheAdresseEnCours(false));
        }, 350);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [adresseQuery, edition, adresseManuelle]);

    // ── Récupération de l'événement existant ───────────────
    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const data = await getEventById(id, token);

                setNomEven(data.nomEven || "");
                setSousNomEven(data.sousNomEven || "");
                setDateTimeEven(data.dateEven || "");
                setDuree(data.duree || "");
                setIsGratuit(!!data.isGratuit);
                setTypeEvent(data.typeEvent || "Présentiel");
                setLienReunion(data.lien_reunion || "");
                setCodePostalEven(data.codePostalEven || "");
                setVilleEven(data.villeEven || "");
                setRueEven(data.rueEven || "");
                setIsRestaurant(!!data.isRestaurant);
                setIsParking(!!data.isParking);
                setIsAuditive(!!data.isAuditive);
                setIsHandicap(!!data.isHandicap);
                setDescription(data.description || "");
                setSelectedCategory({ id: data.categorieID, nomCat: data.categorieNom });

                const type = data.billetterie_type || "externe";
                setBilletterieType(type === "aucune" ? "externe" : type);
                setLienBilletterie(data.lien_billetterie || data.site || "");
                setNbrPlacesMax(data.nbr_places_max != null ? String(data.nbr_places_max) : "");

                const prixRaw = data.prixEven || "";
                if (!data.isGratuit && prixRaw.startsWith("[")) {
                    try {
                        const parsed = JSON.parse(prixRaw);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            setTarifs(parsed.map((t) => ({ titre: t.titre || "", prix: String(t.prix || "") })));
                        }
                    } catch (_) {
                        setTarifs([{ titre: "Billet", prix: extraireNombreDuTexte(prixRaw) }]);
                    }
                } else if (!data.isGratuit && prixRaw && prixRaw !== "0") {
                    setTarifs([{ titre: "Billet", prix: extraireNombreDuTexte(prixRaw) }]);
                }

                let imgs = [];
                if (data.imgEven && data.imgEven !== "empty") imgs = data.imgEven.split("#");
                const filledPreviews = Array(MIN_IMAGES + SECONDARY_IMAGES).fill(null);
                imgs.forEach((url, index) => {
                    if (index < MIN_IMAGES + SECONDARY_IMAGES) filledPreviews[index] = url;
                });
                setPreviewUrls(filledPreviews);
            } catch (err) {
                console.error(err);
                toast.error("Erreur lors de la récupération de l'événement");
                navigate(pageRetour);
            } finally {
                setLoadingEvent(false);
            }
        };
        fetchEvent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // ══════════════════════════════════════════════════════
    //  OUVERTURE D'UNE ÉDITION — pré-remplit la copie de travail
    // ══════════════════════════════════════════════════════

    const ouvrirEdition = (champ) => {
        setEdition(champ);
        switch (champ) {
            case "nom": setSaisie(nomEven); break;
            case "sousnom": setSaisie(sousNomEven); break;
            case "date": setSaisie(dateTimeEven); break;
            case "duree": setSaisie(duree); break;
            case "description": setSaisie(description); break;
            case "billetterie_lien": setSaisie(lienBilletterie); break;
            case "typeEvent": setTypeEventEdition(typeEvent || "Présentiel"); break;
            case "lien_reunion": setLienReunionEdition(lienReunion); break;
            case "adresse":
                setCpEdition(codePostalEven);
                setVilleEditionVal(villeEven);
                setRueEditionVal(rueEven);
                setAdresseQuery("");
                setSuggestionsAdresse([]);
                setAdresseManuelle(false);
                break;
            case "acces":
                setAccesEdition({
                    isRestaurant, isParking, isAuditive, isHandicap,
                });
                break;
            case "tarif":
                setGratuitEdition(isGratuit);
                setTarifsEdition([...tarifs]);
                setNouveauTarifTitre("");
                setNouveauTarifPrix("");
                break;
            case "billetterie_type":
                setBilletterieTypeEdition(billetterieType);
                setLienEdition(lienBilletterie);
                break;
            default: break;
        }
    };

    // ══════════════════════════════════════════════════════
    //  SAUVEGARDE DE CHAQUE ÉDITION (locale, pas d'appel API —
    //  l'envoi global se fait via "Enregistrer les modifications")
    // ══════════════════════════════════════════════════════

    const sauverChampTexte = (regleId, setter) => {
        if (!valider(regleId, saisie)) return;
        setter(saisie.trim());
        setEdition(null);
    };

    const sauverAdresse = () => {
        if (!valider("cp", cpEdition)) return;
        if (!valider("ville", villeEditionVal)) return;
        if (!valider("rue", rueEditionVal)) return;
        setCodePostalEven(cpEdition.trim());
        setVilleEven(villeEditionVal.trim());
        setRueEven(rueEditionVal.trim());
        setEdition(null);
    };

    // Sélection d'une suggestion d'adresse (autocomplétion)
    const choisirSuggestionAdresse = (feature) => {
        const p = feature.properties || {};
        setCpEdition(p.postcode || "");
        setVilleEditionVal(p.city || "");
        setRueEditionVal(p.name || p.label || "");
        setCodePostalEven(p.postcode || "");
        setVilleEven(p.city || "");
        setRueEven(p.name || p.label || "");
        setAdresseQuery("");
        setSuggestionsAdresse([]);
        setEdition(null);
    };

    const sauverTypeEvent = () => {
        setTypeEvent(typeEventEdition);
        if (typeEventEdition === "En Ligne") {
            // Une adresse n'a plus de sens pour un événement en ligne
            setCodePostalEven("");
            setVilleEven("");
            setRueEven("");
        } else {
            // Un lien de réunion n'a plus de sens en présentiel
            setLienReunion("");
        }
        setEdition(null);
    };

    const sauverLienReunion = () => {
        if (!valider("lien_reunion", lienReunionEdition)) return;
        setLienReunion(lienReunionEdition.trim());
        setEdition(null);
    };

    const sauverDate = () => {
        if (!saisie) { toast.error("La date est obligatoire."); return; }
        setDateTimeEven(saisie);
        setEdition(null);
    };

    const sauverDescription = () => {
        const texteBrut = stripHtml(saisie);
        if (!texteBrut) { toast.error("Ce champ est obligatoire."); return; }
        if (texteBrut.length < 20 || texteBrut.length > 2000) {
            toast.error(REGLES.description.message);
            return;
        }
        setDescription(saisie);
        setEdition(null);
    };

    const sauverAcces = () => {
        setIsRestaurant(!!accesEdition.isRestaurant);
        setIsParking(!!accesEdition.isParking);
        setIsAuditive(!!accesEdition.isAuditive);
        setIsHandicap(!!accesEdition.isHandicap);
        setEdition(null);
    };

    const sauverBilletterieType = () => {
        if (billetterieTypeEdition === "externe" && !valider("billetterie_lien", lienEdition)) return;
        setBilletterieType(billetterieTypeEdition);
        setLienBilletterie(billetterieTypeEdition === "externe" ? lienEdition.trim() : lienBilletterie);
        setEdition(null);
    };

    const ajouterTarifEdition = () => {
        if (!valider("tarif_titre", nouveauTarifTitre)) return;
        const val = parseFloat(nouveauTarifPrix.replace(",", "."));
        if (isNaN(val) || val < 0) { toast.error("Entre un prix valide."); return; }
        if (val > 9999) { toast.error("Le prix ne peut pas dépasser 9999 €."); return; }
        setTarifsEdition((prev) => [...prev, { titre: nouveauTarifTitre.trim(), prix: val.toFixed(2) }]);
        setNouveauTarifTitre("");
        setNouveauTarifPrix("");
    };

    const retirerTarifEdition = (index) => {
        setTarifsEdition((prev) => prev.filter((_, i) => i !== index));
    };

    const sauverTarifs = () => {
        if (!gratuitEdition && tarifsEdition.length === 0) {
            toast.error("Ajoute au moins un tarif, ou passe l'événement en gratuit.");
            return;
        }
        setIsGratuit(gratuitEdition);
        setTarifs(gratuitEdition ? [] : tarifsEdition);
        setEdition(null);
    };

    // ── Images ───────────────────────────────────────────────

    const handleImageFile = (file, index) => {
        if (!file) return;
        if (file.size > TAILLE_MAX_IMAGE) {
            const tailleMo = (file.size / (1024 * 1024)).toFixed(1);
            toast.error(`Image trop lourde (${tailleMo} Mo). Maximum autorisé : 2 Mo.`);
            return;
        }
        const url = URL.createObjectURL(file);
        const newPreview = [...previewUrls];
        const newImages = [...images];
        newPreview[index] = url;
        newImages[index] = file;
        setPreviewUrls(newPreview);
        setImages(newImages);
    };

    const retirerImage = (index, e) => {
        e.stopPropagation();
        const newPreview = [...previewUrls];
        const newImages = [...images];
        newPreview[index] = null;
        newImages[index] = null;
        setPreviewUrls(newPreview);
        setImages(newImages);
    };

    // ── Soumission finale (updateEvent) ─────────────────────

    const envoyerEvenement = async () => {
        if (envoiEnCoursRef.current) return;

        const imageTropLourde = images.find((img) => img && img.size > TAILLE_MAX_IMAGE);
        if (imageTropLourde) {
            const tailleMo = (imageTropLourde.size / (1024 * 1024)).toFixed(1);
            toast.error(`Une image dépasse 2 Mo (${tailleMo} Mo). Remplace-la avant d'envoyer.`);
            return;
        }

        envoiEnCoursRef.current = true;
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("id", id);

            const prixEvenJson = isGratuit ? "0" : (tarifs.length ? JSON.stringify(tarifs) : "");

            formData.append("categorieID", selectedCategory.id);
            formData.append("nomEven", nomEven);
            formData.append("sousNomEven", sousNomEven);
            formData.append("dateTimeEven", dateTimeEven);
            formData.append("duree", duree);
            formData.append("prixEven", prixEvenJson);

            formData.append("typeEvent", typeEvent || "Présentiel");
            if (typeEvent === "En Ligne") {
                formData.append("lien_reunion", lienReunion);
            } else {
                formData.append("codePostalEven", codePostalEven);
                formData.append("villeEven", villeEven);
                formData.append("rueEven", rueEven);
            }
            formData.append("description", description);

            formData.append("site", billetterieType === "externe" ? lienBilletterie : "");

            formData.append("isGratuit", isGratuit ? 1 : 0);
            formData.append("isRestaurant", isRestaurant ? 1 : 0);
            formData.append("isParking", isParking ? 1 : 0);
            formData.append("isAuditive", isAuditive ? 1 : 0);
            formData.append("isHandicap", isHandicap ? 1 : 0);

            formData.append("billetterie_type", billetterieType);
            if (billetterieType === "externe" && lienBilletterie.trim()) {
                formData.append("lien_billetterie", lienBilletterie);
            }
            if (billetterieType === "thrundrz" && nbrPlacesMax !== "") {
                formData.append("nbr_places_max", nbrPlacesMax);
            }

            images.forEach((file) => { if (file) formData.append("images[]", file); });
            previewUrls.forEach((url, index) => {
                if (url && !images[index]) formData.append("existingImages[]", url);
            });

            await updateEvent(formData, token);
            toast.success("Événement mis à jour !");
            setTimeout(() => navigate(pageRetour), 1000);
        } catch (err) {
            console.error(err);
            // 423 = le serveur a refusé : billets vendus
            if (err?.response?.status === 423 || err?.response?.data?.code === "EVENEMENT_VERROUILLE") {
                toast.error(err.response.data.message);
                chargerVerrou();
            } else {
                toast.error("Erreur lors de la mise à jour.");
            }
        } finally {
            setIsSubmitting(false);
            envoiEnCoursRef.current = false;
        }
    };

    const formatDateTime = (iso) => {
        if (!iso) return "";
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).replace(",", " à");
    };

    // ══════════════════════════════════════════════════════
    //  LOADING
    // ══════════════════════════════════════════════════════
    const imagePrincipale = images[0] ? URL.createObjectURL(images[0]) : previewUrls[0];

    // ══════════════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════════════

    // ══════════════════════════════════════════════════════
    //  ÉDITEUR ACTIF — rendu dans la zone de saisie fixée en
    //  bas (comme AddEvent2), pas dans la bulle elle-même.
    // ══════════════════════════════════════════════════════

    const renderEditeur = () => {
        switch (edition) {

            case "images":
                return (
                    <div className="flex flex-col gap-3">
                        <div className="relative">
                            {previewUrls[0] ? (
                                <img src={imagePrincipale} alt="" className="h-32 w-full object-cover rounded-xl" />
                            ) : (
                                <div className="h-32 w-full rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center text-white/30 text-sm">
                                    Aucune image
                                </div>
                            )}
                            <label className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-lg cursor-pointer hover:bg-black/80 transition">
                                Changer
                                <input type="file" accept="image/*" className="hidden"
                                    onChange={(e) => handleImageFile(e.target.files[0], 0)} />
                            </label>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="relative w-16 h-16 rounded-lg border-2 border-dashed border-white/20 overflow-hidden">
                                    <label className="w-full h-full flex items-center justify-center cursor-pointer">
                                        {previewUrls[i] ? (
                                            <img src={images[i] ? URL.createObjectURL(images[i]) : previewUrls[i]} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-white/20 text-xl">+</span>
                                        )}
                                        <input type="file" accept="image/*" className="hidden"
                                            onChange={(e) => handleImageFile(e.target.files[0], i)} />
                                    </label>
                                    {previewUrls[i] && (
                                        <button onClick={(e) => retirerImage(i, e)}
                                            className="absolute top-0 right-0 bg-red-600/90 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-bl-lg">✕</button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <BarreEdition onCancel={() => setEdition(null)} onSave={() => setEdition(null)} />
                    </div>
                );

            case "category":
                return (
                    <div className="flex flex-col gap-2">
                        {loadingCategories ? (
                            <p className="text-white/40 text-sm">Chargement…</p>
                        ) : erreurCategories ? (
                            <button onClick={chargerCategories} className="text-sm text-white/60 underline">Réessayer</button>
                        ) : (
                            <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto">
                                {categories.map((cat) => (
                                    <button
                                        key={cat.id}
                                        onClick={() => { setSelectedCategory(cat); setEdition(null); }}
                                        className={`text-left px-3 py-2 rounded-xl border text-sm transition ${selectedCategory?.id === cat.id
                                            ? "border-color1 bg-white/10 text-white"
                                            : "border-white/15 text-white/80 hover:bg-white/10"
                                            }`}
                                    >
                                        {cat.nomCat || cat.nom || cat.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        <button onClick={() => setEdition(null)} className="text-xs text-white/40 mt-1">Annuler</button>
                    </div>
                );

            case "nom":
                return (
                    <div className="flex flex-col gap-2">
                        <input
                            autoFocus value={saisie} maxLength={REGLES.nom.maxLength}
                            onChange={(e) => setSaisie(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && sauverChampTexte("nom", setNomEven)}
                            className="w-full bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1"
                        />
                        <BarreEdition onCancel={() => setEdition(null)} onSave={() => sauverChampTexte("nom", setNomEven)} />
                    </div>
                );

            case "sousnom":
                return (
                    <div className="flex flex-col gap-2">
                        <input
                            autoFocus value={saisie} maxLength={REGLES.sousnom.maxLength}
                            onChange={(e) => setSaisie(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && sauverChampTexte("sousnom", setSousNomEven)}
                            placeholder="1 ou 2 mots maximum"
                            className="w-full bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1"
                        />
                        <BarreEdition onCancel={() => setEdition(null)} onSave={() => sauverChampTexte("sousnom", setSousNomEven)} />
                    </div>
                );

            case "date":
                return (
                    <div className="flex flex-col gap-2">
                        <input
                            type="datetime-local" autoFocus
                            value={saisie ? saisie.slice(0, 16) : ""}
                            onChange={(e) => setSaisie(e.target.value)}
                            className="w-full bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1 [color-scheme:dark]"
                        />
                        <BarreEdition onCancel={() => setEdition(null)} onSave={sauverDate} />
                    </div>
                );

            case "duree":
                return (
                    <div className="flex flex-col gap-2">
                        <input
                            autoFocus value={saisie} maxLength={REGLES.duree.maxLength}
                            onChange={(e) => setSaisie(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && sauverChampTexte("duree", setDuree)}
                            placeholder="Ex : 2h30, 1 jour"
                            className="w-full bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1"
                        />
                        <BarreEdition onCancel={() => setEdition(null)} onSave={() => sauverChampTexte("duree", setDuree)} />
                    </div>
                );

            case "tarif":
                return (
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-2">
                            <button
                                onClick={() => setGratuitEdition(true)}
                                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${gratuitEdition ? "bg-green-600 text-white" : "bg-white/10 text-white/60"}`}
                            >
                                Gratuit
                            </button>
                            <button
                                onClick={() => setGratuitEdition(false)}
                                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${!gratuitEdition ? "bg-gray-500 text-white" : "bg-white/10 text-white/60"}`}
                            >
                                Payant
                            </button>
                        </div>

                        {!gratuitEdition && (
                            <>
                                {tarifsEdition.map((t, i) => (
                                    <div key={i} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2">
                                        <span className="text-white text-sm">{t.titre} — {t.prix} €</span>
                                        <button onClick={() => retirerTarifEdition(i)} className="text-red-400 hover:text-red-300">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                                <div className="flex gap-2">
                                    <input
                                        value={nouveauTarifTitre}
                                        onChange={(e) => setNouveauTarifTitre(e.target.value)}
                                        placeholder="Titre (ex: Adulte)"
                                        className="flex-1 bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1"
                                    />
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={nouveauTarifPrix}
                                        onChange={(e) => setNouveauTarifPrix(e.target.value)}
                                        placeholder="Prix €"
                                        className="w-24 bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1"
                                    />
                                    <button onClick={ajouterTarifEdition}
                                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20 shrink-0">
                                        <Plus size={18} />
                                    </button>
                                </div>
                            </>
                        )}
                        <BarreEdition onCancel={() => setEdition(null)} onSave={sauverTarifs} />
                    </div>
                );

            case "typeEvent":
                return (
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                            <button onClick={() => setTypeEventEdition("Présentiel")}
                                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${typeEventEdition === "Présentiel" ? "text-white" : "bg-white/10 text-white/60"}`}
                                style={typeEventEdition === "Présentiel" ? { backgroundColor: "#481699" } : {}}>
                                📍 Présentiel
                            </button>
                            <button onClick={() => setTypeEventEdition("En Ligne")}
                                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${typeEventEdition === "En Ligne" ? "bg-gray-500 text-white" : "bg-white/10 text-white/60"}`}>
                                💻 En ligne
                            </button>
                        </div>
                        {typeEventEdition === "En Ligne" && (
                            <p className="text-[11px] text-white/40 px-1">
                                L'adresse sera retirée ; pense à renseigner le lien de réunion juste après.
                            </p>
                        )}
                        <BarreEdition onCancel={() => setEdition(null)} onSave={sauverTypeEvent} />
                    </div>
                );

            case "lien_reunion":
                return (
                    <div className="flex flex-col gap-2">
                        <input value={lienReunionEdition} onChange={(e) => setLienReunionEdition(e.target.value)}
                            maxLength={REGLES.lien_reunion.maxLength} placeholder="https://meet.google.com/xxx"
                            className="w-full bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1" />
                        <BarreEdition onCancel={() => setEdition(null)} onSave={sauverLienReunion} />
                    </div>
                );

            case "adresse":
                return (
                    <div className="flex flex-col gap-2">
                        {!adresseManuelle ? (
                            <>
                                <input
                                    type="text" autoFocus
                                    value={adresseQuery}
                                    onChange={(e) => setAdresseQuery(e.target.value)}
                                    placeholder="Ex : 12 rue de la Liberté, Dijon"
                                    className="w-full bg-white/10 border border-white/15 text-white placeholder-white/30 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1"
                                />
                                {rechercheAdresseEnCours && (
                                    <span className="text-[11px] text-white/40 px-1">Recherche…</span>
                                )}
                                {suggestionsAdresse.length > 0 && (
                                    <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                                        {suggestionsAdresse.map((f, i) => (
                                            <button
                                                key={i}
                                                onClick={() => choisirSuggestionAdresse(f)}
                                                className="text-left px-3 py-2 rounded-lg border border-white/15 hover:border-color1 hover:bg-white/10 transition text-sm text-white"
                                            >
                                                {f.properties?.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <p className="text-white/30 text-xs px-1">
                                    Adresse actuelle : {rueEven}, {codePostalEven} {villeEven}
                                </p>
                                <button
                                    onClick={() => setAdresseManuelle(true)}
                                    className="text-xs text-white/50 underline hover:text-white self-start"
                                >
                                    Je préfère saisir l'adresse moi-même
                                </button>
                            </>
                        ) : (
                            <>
                                <input value={rueEditionVal} onChange={(e) => setRueEditionVal(e.target.value)}
                                    maxLength={REGLES.rue.maxLength} placeholder="Rue"
                                    className="w-full bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1" />
                                <div className="flex gap-2">
                                    <input value={cpEdition} onChange={(e) => setCpEdition(e.target.value)}
                                        maxLength={5} placeholder="Code postal"
                                        className="w-1/3 bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1" />
                                    <input value={villeEditionVal} onChange={(e) => setVilleEditionVal(e.target.value)}
                                        maxLength={REGLES.ville.maxLength} placeholder="Ville"
                                        className="flex-1 bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1" />
                                </div>
                                <BarreEdition onCancel={() => setEdition(null)} onSave={sauverAdresse} />
                                <button
                                    onClick={() => setAdresseManuelle(false)}
                                    className="text-xs text-white/50 underline hover:text-white self-start"
                                >
                                    Revenir à la recherche automatique
                                </button>
                            </>
                        )}
                    </div>
                );

            case "acces":
                return (
                    <div className="flex flex-col gap-2">
                        {[
                            ["isRestaurant", "Restaurant sur place"],
                            ["isParking", "Parking disponible"],
                            ["isAuditive", "Accessibilité auditive"],
                            ["isHandicap", "Accessibilité handicap"],
                        ].map(([cle, label]) => (
                            <label key={cle} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2 cursor-pointer">
                                <span className="text-white text-sm">{label}</span>
                                <input
                                    type="checkbox"
                                    checked={!!accesEdition[cle]}
                                    onChange={(e) => setAccesEdition((p) => ({ ...p, [cle]: e.target.checked }))}
                                    className="w-5 h-5 accent-[#7C3AED]"
                                />
                            </label>
                        ))}
                        <BarreEdition onCancel={() => setEdition(null)} onSave={sauverAcces} />
                    </div>
                );

            case "billetterie_type":
                return (
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                            <button onClick={() => setBilletterieTypeEdition("externe")}
                                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${billetterieTypeEdition === "externe" ? "bg-gray-500 text-white" : "bg-white/10 text-white/60"}`}>
                                Lien externe
                            </button>
                            <button onClick={() => setBilletterieTypeEdition("thrundrz")}
                                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${billetterieTypeEdition === "thrundrz" ? "text-white" : "bg-white/10 text-white/60"}`}
                                style={billetterieTypeEdition === "thrundrz" ? { backgroundColor: "#481699" } : {}}>
                                Billetterie Thrundrz
                            </button>
                        </div>
                        {billetterieTypeEdition === "externe" && (
                            <input value={lienEdition} onChange={(e) => setLienEdition(e.target.value)}
                                placeholder="https://..."
                                className="w-full bg-white/10 border border-white/15 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-color1" />
                        )}
                        <BarreEdition onCancel={() => setEdition(null)} onSave={sauverBilletterieType} />
                    </div>
                );

            case "description":
                return (
                    <div className="flex flex-col gap-2">
                        <RichTextEditor value={saisie} onChange={setSaisie} />
                        <span className="text-[11px] text-white/30 px-1">
                            {stripHtml(saisie).length}/{REGLES.description.maxLength}
                        </span>
                        <BarreEdition onCancel={() => setEdition(null)} onSave={sauverDescription} />
                    </div>
                );

            default:
                return null;
        }
    };

    // ══════════════════════════════════════════════════════
    //  LOADING
    // ══════════════════════════════════════════════════════

    if (loadingEvent) {
        return (
            <div className="w-full flex items-center justify-center bg-white min-h-screen">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="animate-spin w-10 h-10" style={{ color: "#B48EF5" }} />
                    <p className="text-black text-sm">Chargement de l'événement…</p>
                </div>
            </div>
        );
    }

    // ══════════════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════════════

    return (
        <div className="w-full flex justify-center bg-white min-h-screen py-4 px-2 lg:py-8 lg:px-4">
            <ToastContainer position="top-right" autoClose={3000} />

            {demande && (
                <ModaleDemande
                    evenementId={id}
                    nbReservations={verrou.nb_reservations}
                    typeInitial={demande}
                    onFermer={() => setDemande(null)}
                    onEnvoye={(msg) => {
                        toast.success(msg);
                        setDemande(null);
                        chargerVerrou();
                    }}
                />
            )}

            <div className="w-full lg:max-w-[700px] flex flex-col h-[92vh] lg:h-[85vh] bg-color4 border border-white/10 shadow-xl rounded-2xl overflow-hidden">

                {/* En-tête fixe */}
                <div className="px-4 py-3 border-b border-white/10 shrink-0">
                    <h1 className="text-white text-lg font-bold">Modifier l'événement</h1>
                    {createurActif && (
                        <p className="text-xs mt-0.5" style={{ color: "#B48EF5" }}>
                            Pour : <b>{createurActif.nom}</b>
                        </p>
                    )}
                    <p className="text-white/40 text-xs mt-0.5">
                        Clique sur ✏️ à côté d'une réponse pour la modifier.
                    </p>
                </div>

                {/* 🔒 Billets vendus : bandeau + contact Thrundrz */}
                {bloque && (
                    <div className="mx-4 mt-3 rounded-xl border border-amber-300/30 bg-amber-400/10 px-3 py-2.5 shrink-0">
                        <p className="text-amber-100 text-xs leading-relaxed">
                            <Lock size={12} className="inline mr-1 -mt-0.5" />
                            <b>{verrou.nb_reservations} réservation(s)</b> : la date, le lieu, les tarifs et la billetterie
                            ne sont plus modifiables. Le nom, les photos et la description restent libres.
                        </p>
                        {verrou.demande_en_cours ? (
                            <p className="text-amber-200/80 text-[11px] mt-1.5">
                                ⏳ Demande envoyée, l'équipe Thrundrz revient vers vous.
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-2 mt-2">
                                <button onClick={() => setDemande("report")}
                                    className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-[11px] font-semibold hover:bg-white/20">
                                    Demander un report
                                </button>
                                <button onClick={() => setDemande("annulation")}
                                    className="px-3 py-1.5 rounded-lg bg-white/10 text-red-200 text-[11px] font-semibold hover:bg-white/20">
                                    Demander l'annulation
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Conversation — SEULE cette zone défile */}
                <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">

                    <LigneRecap label="Photo principale et secondaires ?" testId="btn-edit-images"
                        actif={edition === "images"} onEdit={() => setEdition("images")}
                    >
                        <div className="flex gap-2 flex-wrap">
                            {[0, 1, 2, 3, 4].map((i) => previewUrls[i] && (
                                <img key={i} src={previewUrls[i]} alt="" className="w-10 h-10 rounded-lg object-cover border border-white/20" />
                            ))}
                            {!previewUrls.some(Boolean) && "Aucune image"}
                        </div>
                    </LigneRecap>

                    <LigneRecap label="Quelle catégorie ?" testId="btn-edit-categorie"
                        actif={edition === "category"} onEdit={() => ouvrirEdition("category")}
                    >
                        {selectedCategory?.nomCat}
                    </LigneRecap>

                    <LigneRecap label="Quel est le nom de l'événement ?" testId="btn-edit-nom"
                        actif={edition === "nom"} onEdit={() => ouvrirEdition("nom")}
                    >
                        {nomEven}
                    </LigneRecap>

                    <LigneRecap label="Et le sous-titre ?" testId="btn-edit-sousnom"
                        actif={edition === "sousnom"} onEdit={() => ouvrirEdition("sousnom")}
                    >
                        {sousNomEven}
                    </LigneRecap>

                    <LigneRecap label="Quelle est la date et l'heure ?" testId="btn-edit-date" verrouille={bloque} onVerrou={() => setDemande("report")}
                        actif={edition === "date"} onEdit={() => ouvrirEdition("date")}
                    >
                        {formatDateTime(dateTimeEven)}
                    </LigneRecap>

                    <LigneRecap label="Quelle est la durée ?" testId="btn-edit-duree"
                        actif={edition === "duree"} onEdit={() => ouvrirEdition("duree")}
                    >
                        {duree}
                    </LigneRecap>

                    <LigneRecap label="Et côté tarifs ?" testId="btn-edit-tarif" verrouille={bloque} onVerrou={() => setDemande("modification")}
                        actif={edition === "tarif"} onEdit={() => ouvrirEdition("tarif")}
                    >
                        {isGratuit
                            ? "Gratuit"
                            : tarifs.length > 0
                                ? tarifs.map(t => `${t.titre} : ${t.prix} €`).join(" · ")
                                : null}
                    </LigneRecap>

                    <LigneRecap label="Présentiel ou en ligne ?" testId="btn-edit-typeevent" verrouille={bloque} onVerrou={() => setDemande("modification")}
                        actif={edition === "typeEvent"} onEdit={() => ouvrirEdition("typeEvent")}
                    >
                        {typeEvent === "En Ligne" ? "💻 En ligne" : "📍 Présentiel"}
                    </LigneRecap>

                    {typeEvent === "En Ligne" ? (
                        <LigneRecap label="Lien de connexion ?" testId="btn-edit-lien-reunion" verrouille={bloque} onVerrou={() => setDemande("modification")}
                            actif={edition === "lien_reunion"} onEdit={() => ouvrirEdition("lien_reunion")}
                        >
                            {lienReunion}
                        </LigneRecap>
                    ) : (
                        <LigneRecap label="À quelle adresse ?" testId="btn-edit-adresse" verrouille={bloque} onVerrou={() => setDemande("modification")}
                            actif={edition === "adresse"} onEdit={() => ouvrirEdition("adresse")}
                        >
                            {rueEven}, {codePostalEven} {villeEven}
                        </LigneRecap>
                    )}

                    {typeEvent !== "En Ligne" && (
                        <LigneRecap label="Accessibilité du lieu ?" testId="btn-edit-acces"
                            actif={edition === "acces"} onEdit={() => ouvrirEdition("acces")}
                        >
                            {[isRestaurant && "Restaurant", isParking && "Parking", isAuditive && "Auditif", isHandicap && "Handicap"]
                                .filter(Boolean).join(", ") || "Aucune"}
                        </LigneRecap>
                    )}

                    <LigneRecap label="Comment on réserve ?" testId="btn-edit-billetterie" verrouille={bloque} onVerrou={() => setDemande("modification")}
                        actif={edition === "billetterie_type"} onEdit={() => ouvrirEdition("billetterie_type")}
                    >
                        {billetterieType === "externe" ? `Lien externe : ${lienBilletterie}` : "Billetterie Thrundrz"}
                    </LigneRecap>

                    <LigneRecap label="Décris l'événement" testId="btn-edit-description"
                        actif={edition === "description"} onEdit={() => ouvrirEdition("description")}
                    >
                        <div className="prose prose-invert prose-sm max-w-none line-clamp-3"
                            dangerouslySetInnerHTML={{ __html: description || "—" }} />
                    </LigneRecap>

                </div>
                {/* fin de la zone scrollable */}

                {/* ══════════ ZONE DE SAISIE FIXE — comme AddEvent2 ══════════ */}
                <div className="px-4 py-3 border-t border-white/10 shrink-0">
                    {edition ? (
                        renderEditeur()
                    ) : (
                        <button
                            disabled={isSubmitting}
                            onClick={envoyerEvenement}
                            data-testid="btn-enregistrer-modifications"
                            className="w-full py-3.5 rounded-2xl text-white font-bold text-base disabled:opacity-50 transition"
                            style={{ backgroundColor: "#481699" }}
                        >
                            {isSubmitting ? "Enregistrement..." : "Enregistrer les modifications"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpdateEvent2;